'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { reportesService, ExcepcionesPoliticasData, ExpedientesFaltantesData } from '@/lib/api/reportesService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';

type Accion = 'Excepciones a Políticas' | 'Expedientes Faltantes';

// Obtener primer y último día del mes actual (como legacy)
const getDefaultDates = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const firstDay = new Date(y, m, 1).toISOString().split('T')[0];
  const lastDay = new Date(y, m + 1, 0).toISOString().split('T')[0];
  return { firstDay, lastDay };
};

const CumplimientoManagement: React.FC = () => {
  const { firstDay, lastDay } = getDefaultDates();
  const [accion, setAccion] = useState<Accion>('Excepciones a Políticas');
  const [fechaInicio, setFechaInicio] = useState(firstDay);
  const [fechaFin, setFechaFin] = useState(lastDay);
  const [frecuencia, setFrecuencia] = useState('1'); // 1: Diaria
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataExcepciones, setDataExcepciones] = useState<ExcepcionesPoliticasData | null>(null);
  const [dataExpedientes, setDataExpedientes] = useState<ExpedientesFaltantesData | null>(null);
  const initialLoadDone = useRef(false);

  const load = useCallback(async (accionOverride?: Accion) => {
    const accionActual = accionOverride ?? accion;
    setIsLoading(true);
    setError(null);

    try {
      const res = await reportesService.getCumplimiento({
        accion: accionActual,
        fechaInicio: fechaInicio || undefined,
        fechaFin: fechaFin || undefined,
        frecuencia: frecuencia || undefined,
      });

      if (res.data) {
        if (accionActual === 'Excepciones a Políticas') {
          setDataExcepciones(res.data as ExcepcionesPoliticasData);
          setDataExpedientes(null);
        } else {
          setDataExpedientes(res.data as ExpedientesFaltantesData);
          setDataExcepciones(null);
        }
      } else {
        setError(res.error || 'Error al cargar reporte de cumplimiento');
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar reporte de cumplimiento');
    } finally {
      setIsLoading(false);
    }
  }, [accion, fechaInicio, fechaFin, frecuencia]);

  // Cargar automáticamente al iniciar (como legacy)
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('es-MX', { 
      style: 'currency', 
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }, []);

  // Handler para cambiar acción y recargar (como legacy)
  const handleAccion = useCallback((nuevaAccion: Accion) => {
    setAccion(nuevaAccion);
    setDataExcepciones(null);
    setDataExpedientes(null);
    load(nuevaAccion);
  }, [load]);

  return (
    <>
      <style jsx global>{`
        @media print {
          /* Ocultar elementos no deseados */
          .no-print,
          .no-print * {
            display: none !important;
          }

          /* Ocultar todo excepto el wrapper de impresión y su contenido */
          body * {
            visibility: hidden;
          }

          .print-wrapper,
          .print-wrapper * {
            visibility: visible;
          }

          /* Mostrar el wrapper de impresión ocupando toda la página */
          .print-wrapper {
            position: fixed;
            left: 0;
            top: 0;
            width: 100%;
          }

          /* Reset de todos los contenedores del layout para que no recorten la tabla */
          html, body,
          main, #__next,
          [class*="main"], [class*="content"],
          [class*="container"], [class*="layout"],
          [class*="sidebar"], [class*="Sidebar"],
          nav, aside, header, footer {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            position: static !important;
            left: auto !important;
            transform: none !important;
          }

          /* Estilos para el cuerpo */
          body {
            background: white !important;
            font-size: 12px !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Estilos para contenedores */
          .print-content .bg-white {
            background: white !important;
          }
          
          .print-content .shadow-sm {
            box-shadow: none !important;
          }
          
          .print-content .border {
            border: 1px solid #000 !important;
          }
          
          .print-content .rounded-xl {
            border-radius: 0 !important;
            padding: 0.5rem !important;
            margin-bottom: 1rem !important;
            page-break-inside: avoid;
          }
          
          /* Espaciado entre secciones */
          .print-content .space-y-6 > * + * {
            margin-top: 1.5rem !important;
            page-break-inside: avoid;
          }
          
          /* Preservar colores en filas de totales */
          .print-content .bg-blue-700,
          .print-content tr.bg-blue-700 td {
            background: #1d4ed8 !important;
            color: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-content tr, .print-content td, .print-content th {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Estilos para tablas - CRÍTICO: asegurar que se muestren */
          .print-content table,
          .print-content .print-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 10px !important;
            page-break-inside: auto;
            margin: 0 !important;
            display: table !important;
            visibility: visible !important;
          }

          /* Tablas de detalle (12+ columnas): fuente más pequeña y layout fijo */
          .print-content .print-table.text-xs {
            font-size: 8px !important;
            table-layout: fixed !important;
          }
          
          .print-content thead {
            display: table-header-group !important;
            background: #f3f4f6 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            visibility: visible !important;
          }
          
          .print-content tbody {
            display: table-row-group !important;
            visibility: visible !important;
          }
          
          .print-content tr {
            display: table-row !important;
            page-break-inside: avoid;
            page-break-after: auto;
            visibility: visible !important;
          }
          
          .print-content th, 
          .print-content td {
            border: 1px solid #000 !important;
            padding: 4px 6px !important;
            text-align: left !important;
            word-wrap: break-word;
            display: table-cell !important;
            visibility: visible !important;
          }
          
          .print-content th {
            font-weight: bold !important;
            background: #f3f4f6 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .print-content .overflow-x-auto {
            overflow: visible !important;
            display: block !important;
            visibility: visible !important;
          }
          
          .print-content .print-table-container {
            overflow: visible !important;
            width: 100% !important;
            display: block !important;
            visibility: visible !important;
          }
          
          .print-content .print-section {
            page-break-inside: avoid;
            margin-bottom: 1.5rem !important;
            padding: 0.5rem !important;
            display: block !important;
            visibility: visible !important;
          }
          
          .print-content h2 {
            page-break-after: avoid;
            page-break-inside: avoid;
            margin-bottom: 0.5rem !important;
            font-size: 14px !important;
            font-weight: bold !important;
            display: block !important;
            visibility: visible !important;
          }

          /* Anchos columnas: tabla DETALLE EXCEPCIONES (12 cols) — TOTAL = 100% */
          /* #, PRODUCTO, ASESOR, CLIENTE, NUM CONTROL, ESTATUS, AUTORIZADO, FECHA, EXC1, EXC2, EXC3, RESGUARDO */
          .detalle-excepciones th:nth-child(1),  .detalle-excepciones td:nth-child(1)  { width: 3%; }
          .detalle-excepciones th:nth-child(2),  .detalle-excepciones td:nth-child(2)  { width: 8%; }
          .detalle-excepciones th:nth-child(3),  .detalle-excepciones td:nth-child(3)  { width: 9%; }
          .detalle-excepciones th:nth-child(4),  .detalle-excepciones td:nth-child(4)  { width: 12%; }
          .detalle-excepciones th:nth-child(5),  .detalle-excepciones td:nth-child(5)  { width: 7%; }
          .detalle-excepciones th:nth-child(6),  .detalle-excepciones td:nth-child(6)  { width: 8%; }
          .detalle-excepciones th:nth-child(7),  .detalle-excepciones td:nth-child(7)  { width: 8%; }
          .detalle-excepciones th:nth-child(8),  .detalle-excepciones td:nth-child(8)  { width: 8%; }
          .detalle-excepciones th:nth-child(9),  .detalle-excepciones td:nth-child(9)  { width: 10%; }
          .detalle-excepciones th:nth-child(10), .detalle-excepciones td:nth-child(10) { width: 10%; }
          .detalle-excepciones th:nth-child(11), .detalle-excepciones td:nth-child(11) { width: 10%; }
          .detalle-excepciones th:nth-child(12), .detalle-excepciones td:nth-child(12) { width: 7%; }

          /* Anchos columnas: tabla DETALLE EXPEDIENTES (11 cols) */
          .detalle-expedientes th:nth-child(1), .detalle-expedientes td:nth-child(1) { width: 3%; }
          .detalle-expedientes th:nth-child(2), .detalle-expedientes td:nth-child(2) { width: 9%; }
          .detalle-expedientes th:nth-child(3), .detalle-expedientes td:nth-child(3) { width: 11%; }
          .detalle-expedientes th:nth-child(4), .detalle-expedientes td:nth-child(4) { width: 13%; }
          .detalle-expedientes th:nth-child(5), .detalle-expedientes td:nth-child(5) { width: 12%; }
          .detalle-expedientes th:nth-child(6), .detalle-expedientes td:nth-child(6) { width: 9%; }
          .detalle-expedientes th:nth-child(7), .detalle-expedientes td:nth-child(7) { width: 10%; }
          .detalle-expedientes th:nth-child(8), .detalle-expedientes td:nth-child(8) { width: 9%; }
          .detalle-expedientes th:nth-child(9), .detalle-expedientes td:nth-child(9) { width: 9%; }
          .detalle-expedientes th:nth-child(10), .detalle-expedientes td:nth-child(10) { width: 8%; }
          .detalle-expedientes th:nth-child(11), .detalle-expedientes td:nth-child(11) { width: 7%; }
          
          .print-content .text-xs {
            font-size: 9px !important;
          }
          
          .print-content .text-sm {
            font-size: 10px !important;
          }
          
          @page {
            margin: 0.5cm;
            size: A4 landscape;
          }
        }
      `}</style>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100 no-print">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cumplimiento</h1>
          <p className="text-sm text-gray-500 mt-1">
            Supervisa el cumplimiento de políticas crediticias: Excepciones a Políticas y Expedientes Faltantes
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={accion === 'Excepciones a Políticas' ? 'primary' : 'secondary'}
            onClick={() => handleAccion('Excepciones a Políticas')}
            disabled={isLoading}
          >
            Excepciones a Políticas
          </Button>
          <Button
            variant={accion === 'Expedientes Faltantes' ? 'primary' : 'secondary'}
            onClick={() => handleAccion('Expedientes Faltantes')}
            disabled={isLoading}
          >
            Expedientes Faltantes
          </Button>
        </div>
      </div>

      {error && <div className="no-print"><ErrorMessage message={error} /></div>}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 no-print">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <Input
            label="Fecha inicio"
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
          <Input
            label="Fecha fin"
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={frecuencia}
              onChange={(e) => setFrecuencia(e.target.value)}
            >
              <option value="1">Diaria</option>
              <option value="2">Mensual</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => load()} className="flex-1" disabled={isLoading}>
              {isLoading ? 'Cargando...' : 'Consultar'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.print()}
              className="flex-1"
              disabled={!dataExcepciones && !dataExpedientes || isLoading}
            >
              Imprimir
            </Button>
          </div>
        </div>
      </div>

      <div className="print-wrapper">
        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!isLoading && accion === 'Excepciones a Políticas' && dataExcepciones && (
          <VistaExcepciones data={dataExcepciones} formatCurrency={formatCurrency} />
        )}

        {!isLoading && accion === 'Expedientes Faltantes' && dataExpedientes && (
          <VistaExpedientes data={dataExpedientes} />
        )}

        {!isLoading && !dataExcepciones && !dataExpedientes && !error && (
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center text-gray-500">
            Seleccione un rango de fechas y presione "Consultar" para ver el reporte
          </div>
        )}
      </div>
      </div>
    </>
  );
};

// Componente para vista de Excepciones a Políticas
const VistaExcepciones: React.FC<{ 
  data: ExcepcionesPoliticasData; 
  formatCurrency: (v: number) => string;
}> = ({ data, formatCurrency }) => {
  return (
    <div className="space-y-6 print-content">
      {/* Resumen por Producto */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">RESUMEN POR PRODUCTO</h2>
        <div className="overflow-x-auto print-table-container">
          <table className="min-w-full text-sm print-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left border font-semibold">PRODUCTO</th>
                <th className="px-4 py-3 text-right border font-semibold">CRÉDITOS</th>
                <th className="px-4 py-3 text-right border font-semibold">AUTORIZADO</th>
                <th className="px-4 py-3 text-right border font-semibold">TRANSFERENCIA</th>
                <th className="px-4 py-3 text-right border font-semibold">COMISIONES</th>
                <th className="px-4 py-3 text-right border font-semibold">SEGUROS FINANCIADOS</th>
              </tr>
            </thead>
            <tbody>
              {data.resumenPorProducto.map((resumen, idx) => {
                const isTotal = resumen.producto === 'Total';
                const rowClass = isTotal ? 'bg-blue-700 text-white font-bold' : '';
                return (
                  <tr key={idx} className={rowClass}>
                    <td className="px-4 py-2 border font-medium">{resumen.producto}</td>
                    <td className="px-4 py-2 border text-right">{resumen.totalCreditos}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(resumen.montoTotalAutorizado)}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(resumen.montoTotalTransferencia)}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(resumen.montoTotalComisiones)}</td>
                    <td className="px-4 py-2 border text-right">{formatCurrency(resumen.montoTotalSegurosFinanciados)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen por Asesor/Producto */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">RESUMEN POR ASESOR / PRODUCTO</h2>
        <div className="overflow-x-auto print-table-container">
          <table className="min-w-full text-xs print-table">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan={2} className="px-2 py-2 text-left border font-semibold align-bottom">ASESOR</th>
                <th colSpan={7} className="px-2 py-2 text-center border font-semibold bg-blue-100">NÓMINA</th>
                <th colSpan={7} className="px-2 py-2 text-center border font-semibold bg-green-100">ADELANTO DE NÓMINA</th>
                <th colSpan={6} className="px-2 py-2 text-center border font-semibold bg-yellow-100">SEGURO</th>
              </tr>
              <tr>
                {/* Nómina */}
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">Créditos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">Autorizado</th>
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">Transferencia</th>
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">Nuevos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">%#Nuevos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">%$Nuevos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-blue-50">Comisiones</th>
                {/* Adelanto de Nómina */}
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">Créditos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">Autorizado</th>
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">Transferencia</th>
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">Nuevos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">%#Nuevos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">%$Nuevos</th>
                <th className="px-2 py-2 text-right border font-semibold bg-green-50">Comisiones</th>
                {/* Seguro */}
                <th className="px-2 py-2 text-right border font-semibold bg-yellow-50">25K</th>
                <th className="px-2 py-2 text-right border font-semibold bg-yellow-50">50K</th>
                <th className="px-2 py-2 text-right border font-semibold bg-yellow-50">75K</th>
                <th className="px-2 py-2 text-right border font-semibold bg-yellow-50">100K</th>
                <th className="px-2 py-2 text-right border font-semibold bg-yellow-50">Total</th>
                <th className="px-2 py-2 text-right border font-semibold bg-yellow-50">Financiado</th>
              </tr>
            </thead>
            <tbody>
              {data.resumenPorAsesor.map((resumen, idx) => {
                const isTotal = resumen.asesor === 'Total';
                const rowClass = isTotal ? 'bg-blue-700 text-white font-bold' : 'hover:bg-gray-50';
                const cellBgNomina = isTotal ? '' : 'bg-blue-50/30';
                const cellBgAdelanto = isTotal ? '' : 'bg-green-50/30';
                const cellBgSeguro = isTotal ? '' : 'bg-yellow-50/30';
                return (
                  <tr key={idx} className={rowClass}>
                    <td className="px-2 py-2 border font-medium">{resumen.asesor}</td>
                    {/* Nómina */}
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{resumen.nomina.creditos}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{formatCurrency(resumen.nomina.autorizado)}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{formatCurrency(resumen.nomina.transferencia)}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{resumen.nomina.nuevos}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{resumen.nomina.porcNumNuevos}%</td>
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{resumen.nomina.porcMontoNuevos}%</td>
                    <td className={`px-2 py-2 border text-right ${cellBgNomina}`}>{formatCurrency(resumen.nomina.comisiones)}</td>
                    {/* Adelanto de Nómina */}
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{resumen.adelantoNomina.creditos}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{formatCurrency(resumen.adelantoNomina.autorizado)}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{formatCurrency(resumen.adelantoNomina.transferencia)}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{resumen.adelantoNomina.nuevos}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{resumen.adelantoNomina.porcNumNuevos}%</td>
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{resumen.adelantoNomina.porcMontoNuevos}%</td>
                    <td className={`px-2 py-2 border text-right ${cellBgAdelanto}`}>{formatCurrency(resumen.adelantoNomina.comisiones)}</td>
                    {/* Seguro */}
                    <td className={`px-2 py-2 border text-right ${cellBgSeguro}`}>{resumen.seguro.count25k}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgSeguro}`}>{resumen.seguro.count50k}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgSeguro}`}>{resumen.seguro.count75k}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgSeguro}`}>{resumen.seguro.count100k}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgSeguro}`}>{resumen.seguro.total}</td>
                    <td className={`px-2 py-2 border text-right ${cellBgSeguro}`}>{formatCurrency(resumen.seguro.financiado)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detalle por Cliente - Agrupado por Asesor */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">DETALLE POR CLIENTE</h2>

        {data.detallePorAsesor && data.detallePorAsesor.length > 0 ? (
          data.detallePorAsesor.map((asesorData, asesorIdx) => (
            <div key={asesorIdx} className="mb-6">
              {/* Encabezado del Asesor */}
              <div className="bg-gray-700 text-white px-4 py-2 font-bold text-sm rounded-t">
                {asesorData.asesor}
              </div>

              <div className="overflow-x-auto print-table-container border border-gray-300">
                <table className="min-w-full text-xs print-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left border font-semibold">#</th>
                      <th className="px-2 py-2 text-left border font-semibold">PRODUCTO</th>
                      <th className="px-2 py-2 text-left border font-semibold">CONTRATO IMPRESO</th>
                      <th className="px-2 py-2 text-left border font-semibold">NUM CONTROL</th>
                      <th className="px-2 py-2 text-left border font-semibold">CLIENTE</th>
                      <th className="px-2 py-2 text-right border font-semibold">MONTO AUTORIZADO</th>
                      <th className="px-2 py-2 text-right border font-semibold">MONTO TRANSFERIDO</th>
                      <th className="px-2 py-2 text-right border font-semibold">COMISIÓN</th>
                      <th className="px-2 py-2 text-right border font-semibold">SEGURO FINANCIADO</th>
                      <th className="px-2 py-2 text-left border font-semibold">SEGURO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesorData.registros.map((item: any, idx: number) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-2 py-2 border">{idx + 1}</td>
                        <td className="px-2 py-2 border">{item.producto || '—'}</td>
                        <td className="px-2 py-2 border">
                          {item.fechaContratoImpreso
                            ? new Date(item.fechaContratoImpreso).toLocaleDateString('es-MX')
                            : '—'}
                        </td>
                        <td className="px-2 py-2 border">{item.solicitudNumeroControl || '—'}</td>
                        <td className="px-2 py-2 border">
                          {[item.clienteNombre, item.clienteApellidoPaterno, item.clienteApellidoMaterno]
                            .filter(Boolean)
                            .join(' ') || '—'}
                        </td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(item.montoAutorizado || 0)}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(item.montoTransferencia || 0)}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(item.montoComision || 0)}</td>
                        <td className="px-2 py-2 border text-right">{formatCurrency(item.solicitudSeguroFinanciado || 0)}</td>
                        <td className="px-2 py-2 border">{item.polizaSeguro || '—'}</td>
                      </tr>
                    ))}

                    {/* Fila Total Nómina */}
                    <tr className="bg-blue-600 text-white font-bold">
                      <td className="px-2 py-2 border text-right" colSpan={5}>TOTAL NÓMINA ({asesorData.totalNomina.creditos})</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalNomina.montoAutorizado)}</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalNomina.montoTransferencia)}</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalNomina.comision)}</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalNomina.seguroFinanciado)}</td>
                      <td className="px-2 py-2 border text-xs">
                        {asesorData.totalNomina.seguro25k > 0 && `25k:${asesorData.totalNomina.seguro25k} `}
                        {asesorData.totalNomina.seguro50k > 0 && `50k:${asesorData.totalNomina.seguro50k} `}
                        {asesorData.totalNomina.seguro75k > 0 && `75k:${asesorData.totalNomina.seguro75k} `}
                        {asesorData.totalNomina.seguro100k > 0 && `100k:${asesorData.totalNomina.seguro100k}`}
                      </td>
                    </tr>

                    {/* Fila Total Adelanto */}
                    <tr className="bg-green-600 text-white font-bold">
                      <td className="px-2 py-2 border text-right" colSpan={5}>TOTAL ADELANTO ({asesorData.totalAdelanto.creditos})</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalAdelanto.montoAutorizado)}</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalAdelanto.montoTransferencia)}</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalAdelanto.comision)}</td>
                      <td className="px-2 py-2 border text-right">{formatCurrency(asesorData.totalAdelanto.seguroFinanciado)}</td>
                      <td className="px-2 py-2 border text-xs">
                        {asesorData.totalAdelanto.seguro25k > 0 && `25k:${asesorData.totalAdelanto.seguro25k} `}
                        {asesorData.totalAdelanto.seguro50k > 0 && `50k:${asesorData.totalAdelanto.seguro50k} `}
                        {asesorData.totalAdelanto.seguro75k > 0 && `75k:${asesorData.totalAdelanto.seguro75k} `}
                        {asesorData.totalAdelanto.seguro100k > 0 && `100k:${asesorData.totalAdelanto.seguro100k}`}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500 py-8">
            Sin registros para mostrar
          </div>
        )}

        <div className="mt-4 text-sm text-gray-600 no-print">
          <p>Fecha de reporte: {data.fechaReporte}</p>
        </div>
      </div>
    </div>
  );
};

// Componente para vista de Expedientes Faltantes
const VistaExpedientes: React.FC<{ data: ExpedientesFaltantesData }> = ({ data }) => {
  return (
    <div className="space-y-6 print-content">
      {/* Resumen de Entregas Semanales */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">RESUMEN DE ENTREGA SEMANAL</h2>
        <div className="overflow-x-auto print-table-container">
          <table className="min-w-full text-sm print-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left border font-semibold">AÑO</th>
                <th className="px-4 py-3 text-left border font-semibold">PRODUCTO</th>
                <th className="px-4 py-3 text-right border font-semibold">SEMANA ANTERIOR</th>
                <th className="px-4 py-3 text-right border font-semibold">SEMANA ACTUAL</th>
              </tr>
            </thead>
            <tbody>
              {data.resumenEntregasSemanales.map((resumen, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2 border">{resumen.año}</td>
                  <td className="px-4 py-2 border font-medium">{resumen.producto}</td>
                  <td className="px-4 py-2 border text-right">{resumen.semanaAnterior}</td>
                  <td className="px-4 py-2 border text-right">{resumen.semanaActual}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen por Producto */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">RESUMEN POR PRODUCTO</h2>
        <div className="overflow-x-auto print-table-container">
          <table className="min-w-full text-sm print-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left border font-semibold">AÑO</th>
                <th className="px-4 py-3 text-left border font-semibold">PRODUCTO</th>
                <th className="px-4 py-3 text-right border font-semibold">FONDEADOS</th>
                <th className="px-4 py-3 text-right border font-semibold">RECHAZADOS</th>
                <th className="px-4 py-3 text-right border font-semibold">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {data.resumenPorProducto.map((resumen, idx) => {
                const isTotal = resumen.año === 'Total';
                const rowClass = isTotal ? 'bg-blue-700 text-white font-bold' : '';
                return (
                  <tr key={idx} className={rowClass}>
                    <td className="px-4 py-2 border">{resumen.año}</td>
                    <td className="px-4 py-2 border font-medium">{resumen.producto}</td>
                    <td className="px-4 py-2 border text-right">{resumen.fondeados}</td>
                    <td className="px-4 py-2 border text-right">{resumen.rechazados}</td>
                    <td className="px-4 py-2 border text-right font-semibold">{resumen.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen por Asesor/Producto */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">RESUMEN POR ASESOR / PRODUCTO</h2>
        <div className="overflow-x-auto print-table-container">
          <table className="min-w-full text-sm print-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left border font-semibold">ASESOR</th>
                <th className="px-4 py-3 text-left border font-semibold">PRODUCTO</th>
                <th className="px-4 py-3 text-left border font-semibold">AÑO</th>
                <th className="px-4 py-3 text-right border font-semibold">FONDEADOS</th>
                <th className="px-4 py-3 text-right border font-semibold">RECHAZADOS</th>
                <th className="px-4 py-3 text-right border font-semibold">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {data.resumenPorAsesor.map((resumen, idx) => {
                const isTotal = resumen.asesor === 'Total';
                const rowClass = isTotal ? 'bg-blue-700 text-white font-bold' : '';
                return (
                  <tr key={idx} className={rowClass}>
                    <td className="px-4 py-2 border font-medium">{resumen.asesor}</td>
                    <td className="px-4 py-2 border">{resumen.producto}</td>
                    <td className="px-4 py-2 border">{resumen.año}</td>
                    <td className="px-4 py-2 border text-right">{resumen.fondeados}</td>
                    <td className="px-4 py-2 border text-right">{resumen.rechazados}</td>
                    <td className="px-4 py-2 border text-right font-semibold">{resumen.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detalle por Cliente */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 print-section">
        <h2 className="text-xl font-bold text-gray-900 mb-4">DETALLE POR CLIENTE</h2>
        <div className="mb-4 text-sm text-gray-600 no-print">
          <p>Fecha de exigibilidad: {data.fechaExigibilidad} (15 días hábiles antes de hoy)</p>
        </div>
        <div className="overflow-x-auto print-table-container">
          <table className="min-w-full text-xs print-table detalle-expedientes">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left border font-semibold">#</th>
                <th className="px-2 py-2 text-left border font-semibold">PRODUCTO</th>
                <th className="px-2 py-2 text-left border font-semibold">ASESOR</th>
                <th className="px-2 py-2 text-left border font-semibold">CLIENTE</th>
                <th className="px-2 py-2 text-left border font-semibold">EMPRESA</th>
                <th className="px-2 py-2 text-left border font-semibold">NUM CONTROL</th>
                <th className="px-2 py-2 text-left border font-semibold">ESTATUS</th>
                <th className="px-2 py-2 text-left border font-semibold">FECHA ENTREGA</th>
                <th className="px-2 py-2 text-left border font-semibold">EXP. FALTANTE</th>
                <th className="px-2 py-2 text-left border font-semibold">EXP. EXIGIBLE</th>
                <th className="px-2 py-2 text-left border font-semibold">RESGUARDO</th>
              </tr>
            </thead>
            <tbody>
              {data.detalle.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-2 py-2 border">{idx + 1}</td>
                  <td className="px-2 py-2 border">{item.producto || '—'}</td>
                  <td className="px-2 py-2 border">{item.asesorNombre || '—'}</td>
                  <td className="px-2 py-2 border">
                    {[item.clienteNombre, item.clienteApellidoPaterno, item.clienteApellidoMaterno]
                      .filter(Boolean)
                      .join(' ') || '—'}
                  </td>
                  <td className="px-2 py-2 border">{item.clienteEmpresa || '—'}</td>
                  <td className="px-2 py-2 border">{item.solicitudNumeroControl || '—'}</td>
                  <td className="px-2 py-2 border">{item.solicitudEstatus || '—'}</td>
                  <td className="px-2 py-2 border">
                    {item.fechaEntregaARiesgos ? new Date(item.fechaEntregaARiesgos).toLocaleDateString('es-MX') : '—'}
                  </td>
                  <td className="px-2 py-2 border">
                    {item.expedienteResguardoStatus === 'Comercial' ? 'Sí' : 'No'}
                  </td>
                  <td className="px-2 py-2 border">
                    {item.expedienteExigible || 'No'}
                  </td>
                  <td className="px-2 py-2 border">{item.expedienteResguardoStatus || '—'}</td>
                </tr>
              ))}
              {data.detalle.length === 0 && (
                <tr>
                  <td className="px-4 py-4 text-center text-gray-500" colSpan={11}>
                    Sin expedientes faltantes exigibles
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CumplimientoManagement;
